import { z } from 'zod'

import { now as serverNow } from '~/models/clock'
import {
  JEPX_PRICE_CONSTRAINT,
  JEPX_VOLUME_CONSTRAINT,
  isPlainDecimalInput,
  violatesJepxStep,
} from '~/models/numberInputConstraint'
import {
  type WithdrawAtViolation,
  validateWithdrawAt,
} from '~/pages/IntradayBidding/models/withdrawAt'

/**
 * 時間前の札作成モーダル・入札編集モーダルで共通の入力チェック文言 (spec intraday-bidding.md
 * AC-BIN-052。 スポットと共通の文言 = AC-BSP-078)。 入札価格は未入力と範囲外で同じ文言、
 * 入札量は 0.1 MW 未満・上限超過で同じ文言。
 */
const PRICE_RANGE_MESSAGE = '価格は 0.01 〜 999 円/kWh の範囲で入力してください'
/** 手数料自動考慮が ON の未入力 (AC-BIN-052。 範囲外は算出した入札価格で判定する = AC-BIN-104)。 */
const DESIRED_PRICE_REQUIRED_MESSAGE = '希望価格を入力してください'
const VOLUME_REQUIRED_MESSAGE = '入札量を入力してください'
const VOLUME_RANGE_MESSAGE =
  '入札量は 0.1 〜 9999.9 MW の範囲で入力してください'
const VOLUME_STEP_MESSAGE = `入札量は ${JEPX_VOLUME_CONSTRAINT.step} MW 刻みで入力してください`

/**
 * 入札価格 (円/kWh) の違反文言。 違反なしは `undefined`。 指数表記 (`1e2`) は backend が
 * 入力エラーにするので、 送る前にここで範囲違反として弾く。
 */
export const intradayPriceViolation = (raw: string): string | undefined => {
  const n = Number(raw)
  return !isPlainDecimalInput(raw) ||
    n < JEPX_PRICE_CONSTRAINT.min ||
    n > JEPX_PRICE_CONSTRAINT.max
    ? PRICE_RANGE_MESSAGE
    : undefined
}

/**
 * 手数料自動考慮が ON の希望価格の違反文言。 違反なしは `undefined`。 値域は希望価格に当てず、
 * 算出した入札価格で backend のプレビューが判定する (AC-BIN-104)。
 */
export const intradayDesiredPriceViolation = (
  raw: string,
): string | undefined =>
  isPlainDecimalInput(raw) ? undefined : DESIRED_PRICE_REQUIRED_MESSAGE

/** 入札量 (MW) の違反文言。 違反なしは `undefined`。 */
export const intradayVolumeViolation = (raw: string): string | undefined => {
  if (raw === '') return VOLUME_REQUIRED_MESSAGE
  const n = Number(raw)
  if (
    !isPlainDecimalInput(raw) ||
    n < JEPX_VOLUME_CONSTRAINT.min ||
    n > JEPX_VOLUME_CONSTRAINT.max
  ) {
    return VOLUME_RANGE_MESSAGE
  }
  return violatesJepxStep(n, JEPX_VOLUME_CONSTRAINT.step)
    ? VOLUME_STEP_MESSAGE
    : undefined
}

const updateIntradayOrderFieldsSchema = z.object({
  /** 売/買 (radio の '1' = 買 / '2' = 売。 下書きで変えられる、 AC-BIN-007)。 */
  side: z.string().min(1, '売/買を選択してください'),
  /** 入札価格 (成行は検証しない。 `buildUpdateIntradayOrderFormSchema` が判定する)。 */
  priceYenPerKwh: z.string(),
  volumeMwh: z.string(),
  /** 自動取消時刻 (datetime-local、 任意)。 */
  withdrawAt: z.string(),
  /** メモ（内部用、 100文字以内、 JEPX へは送信しない）。 */
  memoFree: z.string().max(100, 'メモは100文字以内で入力してください'),
  /** JEPX自由記述欄 (70文字以内)。 */
  memoJepx: z.string().max(70, 'JEPX自由記述欄は70文字以内で入力してください'),
})

export type UpdateIntradayOrderFormInput = z.infer<
  typeof updateIntradayOrderFieldsSchema
>

/** フォーミュラ選択時にオフセット値が無い (AC-BIN-052)。 */
const FORMULA_OFFSET_REQUIRED_MESSAGE = 'オフセットを入力してください'

/**
 * フォーミュラのオフセット値の違反文言 (AC-BIN-052)。 違反なしは `undefined`。 札作成・入札編集・
 * 一括作成で共通。 指数表記 (`1e2`) は backend が入力エラーにするので、 未入力と同じく弾く。
 */
export const intradayFormulaOffsetViolation = (
  raw: string,
): string | undefined =>
  isPlainDecimalInput(raw) ? undefined : FORMULA_OFFSET_REQUIRED_MESSAGE

const intradayOrderEditFieldsSchema = updateIntradayOrderFieldsSchema.extend({
  /** 価格の入れ方 (固定 / フォーミュラ。 AC-BIN-007)。 成行の札は固定のまま。 */
  priceType: z.enum(['fixed', 'formula']),
  formulaVariable: z.string(),
  formulaReferenceArea: z.string(),
  formulaOperator: z.string(),
  formulaOffset: z.string(),
})

/** 入札編集モーダル (AC-BIN-007) のフォームの生入力。 価格欄に加えてフォーミュラ欄を持つ。 */
export type IntradayOrderEditFormInput = z.infer<
  typeof intradayOrderEditFieldsSchema
>

/**
 * 入札編集の本文の価格がフォーミュラか。 フォーミュラを選べる時 (編集できる指値の下書きで、 入札方式が
 * 戦略でない時。 戦略の札の価格は段から決まる) に、 フォーミュラを選んでいる場合だけ。
 * 画面の欄の出し分け・送る価格・検証が同じこの判定を使う。
 */
export const isEditFormulaPrice = (
  formulaSelectable: boolean,
  priceType: IntradayOrderEditFormInput['priceType'],
): boolean => formulaSelectable && priceType === 'formula'

/**
 * 時間前注文 更新フォームスキーマ (AC-BIN-007: 下書きの入札編集モーダル)。
 *
 * 自動取消時刻の「現在時刻より後・対象コマの GC まで」は入札追加 modal
 * (`IntradayBidCreateModal` の `buildSchema`) と同じく superRefine で検証し、
 * 他項目のエラーと同時に出す。 現在時刻は検証時に読む。 文言は呼び出し側の `t` から受ける。
 * 成行 (AC-BIN-004d) は価格を持たないので入札価格を検証しない (入力欄は止め、 送信値は
 * write seam が "0" にする)。
 * 手数料自動考慮が ON (希望価格を入力する) の時は、 値域 0.01〜999 を希望価格には当てない
 * (算出した入札価格の値域・入札制限は backend のプレビューが判定する = AC-BIN-104)。
 * フォーミュラの時 (`isEditFormulaPrice`) は価格欄を検証せず、 オフセット値だけを検証する
 * (計算結果の値域は backend が保存時に判定する = AC-BIN-020)。
 */
export const buildUpdateIntradayOrderFormSchema = ({
  withdrawAtMessage,
  koma,
  deliveryDate,
  isMarketOrder,
  feeAutoConsiderOn,
  formulaSelectable,
}: {
  withdrawAtMessage: (violation: WithdrawAtViolation) => string
  koma: number
  deliveryDate: Date
  /** 成行 (AC-BIN-004d) の札なら入札価格を検証しない。 */
  isMarketOrder: boolean
  /** 手数料自動考慮 ON (価格欄が希望価格) の編集か (AC-BIN-004b)。 */
  feeAutoConsiderOn: boolean
  /** フォーミュラを選べるか (`isEditFormulaPrice` の引数と同じ)。 */
  formulaSelectable: boolean
}): z.ZodType<IntradayOrderEditFormInput, IntradayOrderEditFormInput> =>
  intradayOrderEditFieldsSchema.superRefine((v, ctx) => {
    const issue = (path: string, message: string): void => {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message })
    }

    if (isEditFormulaPrice(formulaSelectable, v.priceType)) {
      const offsetMessage = intradayFormulaOffsetViolation(v.formulaOffset)
      if (offsetMessage !== undefined) issue('formulaOffset', offsetMessage)
    } else if (!isMarketOrder) {
      const message = feeAutoConsiderOn
        ? intradayDesiredPriceViolation(v.priceYenPerKwh)
        : intradayPriceViolation(v.priceYenPerKwh)
      if (message !== undefined) issue('priceYenPerKwh', message)
    }
    const volumeMessage = intradayVolumeViolation(v.volumeMwh)
    if (volumeMessage !== undefined) issue('volumeMwh', volumeMessage)

    const violation = validateWithdrawAt({
      withdrawAtLocal: v.withdrawAt,
      koma,
      deliveryDate,
      now: serverNow(),
    })
    if (violation !== undefined) {
      issue('withdrawAt', withdrawAtMessage(violation))
    }
  })
